'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { PageAboutUsSchema } from '@/sanity/pages/PageAboutUsTemplate';
import { GroqImage } from '@/helpers/Image';
import Image from 'next/image';
import heroMobileBg from '@/public/about-us/about-us-hero-mobile-bg.png';
import { PageAssetsSchema } from '@/sanity/pages/PageAssetsTemplate';
import { cn } from '@/lib/utils';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({ data }: { data: PageAssetsSchema }) {
  return (
    <section
      className={cn(
        'relative w-full overflow-x-clip lg:min-h-[80.5rem] lg:justify-center flex flex-col ',
        data.pageStyle === 'crypto' ? 'min-h-[105rem]' : ' min-h-[115rem]',
      )}
    >
      {/* Background — mobile */}
      <motion.div
        initial={{ opacity: 0, filter: 'blur(8px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 1.2, ease }}
        className="absolute inset-0 w-full h-full pointer-events-none block lg:hidden"
        aria-hidden="true"
      >
        <Image
          src={heroMobileBg}
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      {/* Background — desktop */}
      {data?.heroBackgroundImage && (
        <div
          className="absolute w-full h-full inset-0 pointer-events-none hidden lg:block"
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0, filter: 'blur(8px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 1.2, ease }}
            className="w-full h-full"
          >
            <GroqImage
              src={data?.heroBackgroundImage}
              alt=""
              className="!w-full !h-full !object-cover"
            />
          </motion.div>
        </div>
      )}

      <div className="pt-[8rem] lg:py-[1.1rem] flex items-center w-[min(140rem,calc(100%-4rem))] lg:w-[min(160rem,calc(100%-8rem))] mx-auto relative">
        {/* Content */}
        <div className="relative z-10 flex flex-col gap-[3rem] lg:gap-[2.63rem] lg:items-start items-center w-full lg:max-w-[95.25rem] ">
          <div className="flex flex-col gap-[1.75rem] lg:items-start items-center">
            {data?.heroChip && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="mb-0 font-blacker italic text-[2rem] text-black leading-[1.5]"
              >
                {data.heroChip}
              </motion.p>
            )}

            {/* Desktop title */}
            {data?.heroText && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.35 }}
                className="richtext lg:richtext-start prose-p:font-medium gap-[2rem] prose-headings:mb-[1rem] prose-p:[text-wrap-style:balance] max-w-[85rem]"
              >
                <PortableText
                  value={data.heroText}
                  components={richTextComponent}
                />
              </motion.div>
            )}
          </div>

          {data?.heroChecklist && data.heroChecklist.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.4 }}
              className="w-full flex flex-col gap-[2.25rem] lg:items-start items-center"
            >
              <div className="w-full max-w-[67.1875rem] h-px bg-black/10" />
              <ul className="mb-0 w-full p-0 list-none grid grid-cols-1 lg:grid-cols-1 gap-x-[4.875rem] gap-y-[1.5rem] lg:justify-start justify-center">
                {data.heroChecklist.map((item, index) => (
                  <li
                    key={`${item}-${index}`}
                    className="flex items-center gap-[1.5rem]"
                  >
                    <span
                      aria-hidden="true"
                      className="flex items-center justify-center shrink-0 w-[3.0255rem] h-[3.0255rem] lg:w-[3.43875rem] lg:h-[3.43875rem]"
                    >
                      <span className="-rotate-45 flex items-center justify-center shrink-0 bg-[#daf2e0] border-[0.88px] lg:border border-[rgba(109,169,145,0.1)] p-[0.64175rem] lg:p-[0.7295rem]">
                        <span className="block w-[0.85575rem] h-[0.85575rem] lg:w-[0.97263rem] lg:h-[0.97263rem] shrink-0 bg-[#103037]" />
                      </span>
                    </span>
                    <span className="mb-0 font-geist font-medium text-[2rem] leading-[1.5] text-black/70">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {data?.heroCTA && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.5 }}
              className="w-full lg:w-fit"
            >
              <CTAButton data={data.heroCTA} />
            </motion.div>
          )}
        </div>
      </div>
      {/* Person image */}
      {data?.heroImage && (
        <div
          className={cn(
            'absolute lg:right-[15rem] right-auto lg:left-auto left-1/2 lg:-translate-x-0  bottom-0 lg:w-[108rem] w-[55rem] lg:h-auto h-auto pointer-events-none z-10',
            data.pageStyle === 'crypto'
              ? 'lg:w-[120rem]  lg:bottom-[-0.75rem] lg:h-auto -translate-x-1/2'
              : 'lg:w-[95rem] lg:bottom-0 lg:h-auto -translate-x-[46%]',
          )}
          aria-hidden="true"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease, delay: 0.3 }}
            className="w-full h-full"
          >
            <GroqImage
              src={data.heroImage}
              alt={data.heroImage.name || ''}
              className="object-contain object-top !w-full"
            />
          </motion.div>
        </div>
      )}
    </section>
  );
}
